import { Injectable, effect, inject } from '@angular/core';
// Type-only import — erased at compile time so @microsoft/signalr stays out
// of the initial bundle. The runtime members are dynamically imported in
// reconnectFor/ensureConnected so SignalR loads as a lazy chunk only after a
// user is authenticated (anonymous cold starts never pay for it).
import type { HubConnection } from '@microsoft/signalr';

import { AuthService } from './auth.service';
import { TokenBagService } from './token-bag.service';
import { environment } from '../../../environments/environment';

/**
 * MOB-100 Stage 18 — SignalR session-displaced handler.
 *
 * Per Stage 2's `MaxConcurrentSessionsEnforcer` the server pushes a
 * `'session-displaced'` payload on the existing user-{userId} channel
 * whenever a newer login on a different device revokes the older
 * DeviceSession. Mobile renders the friendly Session-Displaced screen
 * and discards the local token — *not* a panic state, per the prompt:
 *
 * > 'Your registration is now signed in on another device. That's
 * > expected for paid events.'
 *
 * The actual SignalR hub is shared with the rest of the platform (see
 * `apps/web/src/app/core/services/realtime.service.ts`); the mobile
 * side connects opportunistically — Stage 18 only needs the displaced
 * event, more hub methods land in later stages.
 *
 * Connection lifecycle:
 *  - Auto-connect after auth (effect on TokenBagService).
 *  - Reconnect on transient errors with exponential backoff.
 *  - Disconnect on logout / displacement / cold-launch teardown.
 */
const HUB_PATH = '/hubs/notifications';
const RECONNECT_DELAYS_MS = [0, 2_000, 5_000, 10_000, 30_000];

@Injectable({ providedIn: 'root' })
export class SessionDisplacedService {
  private readonly auth = inject(AuthService);
  private readonly tokenBag = inject(TokenBagService);

  private connection: HubConnection | null = null;
  /** Last tenant we connected for — drives reconnect-on-switch. */
  private connectedTenantId: number | null = null;

  constructor() {
    // Track the active tenant — every time it changes, tear down the
    // existing hub and connect with the new tenant's access token.
    effect(() => {
      const active = this.tokenBag.active();
      if (!active) {
        void this.disconnect();
        return;
      }
      if (active.tenantId !== this.connectedTenantId) {
        void this.reconnectFor(active.tenantId);
      }
    });
  }

  /** Test-only / hand-driven boot — usually called from APP_INITIALIZER. */
  async ensureConnected(): Promise<void> {
    const active = this.tokenBag.getActive();
    if (!active) return;
    const signalR = await import('@microsoft/signalr');
    if (active.tenantId === this.connectedTenantId &&
        this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }
    await this.reconnectFor(active.tenantId);
  }

  async disconnect(): Promise<void> {
    if (!this.connection) return;
    try {
      await this.connection.stop();
    } catch {
      // Idempotent.
    }
    this.connection = null;
    this.connectedTenantId = null;
  }

  // ---------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------

  private async reconnectFor(tenantId: number): Promise<void> {
    await this.disconnect();
    this.connectedTenantId = tenantId;

    const { HubConnectionBuilder, HttpTransportType, LogLevel } =
      await import('@microsoft/signalr');

    const builder = new HubConnectionBuilder()
      .withUrl(`${environment.apiUrl}${HUB_PATH}`, {
        accessTokenFactory: () => this.auth.getToken() ?? '',
        transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) =>
          RECONNECT_DELAYS_MS[Math.min(ctx.previousRetryCount, RECONNECT_DELAYS_MS.length - 1)]
      })
      .configureLogging(environment.production ? LogLevel.Warning : LogLevel.Information);

    this.connection = builder.build();

    this.connection.on('session-displaced', (payload: unknown) => {
      const displacedId = readDisplacedId(payload);
      const fallback = this.tokenBag.getActive()?.deviceSessionId ?? 0;
      void this.auth.handleSessionDisplaced(displacedId ?? fallback);
    });

    // Same wire as Stage 2 — server may emit either casing in payload.
    this.connection.on('displaced', (payload: unknown) => {
      const displacedId = readDisplacedId(payload);
      const fallback = this.tokenBag.getActive()?.deviceSessionId ?? 0;
      void this.auth.handleSessionDisplaced(displacedId ?? fallback);
    });

    try {
      await this.connection.start();
    } catch (err) {
      console.warn('[SessionDisplacedService] Hub start failed:', err);
    }
  }
}

function readDisplacedId(payload: unknown): number | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  const candidates = [p['displacedDeviceSessionId'], p['deviceSessionId'], p['sessionId']];
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
    if (typeof candidate === 'string' && candidate.length > 0) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}
